import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiUser, FiMail, FiPhone, FiHome, FiMessageSquare } from 'react-icons/fi';
import emailjs from "@emailjs/browser";

const InputField = ({ icon, placeholder, type, name, value, onChange, required }) => {
    return (
        <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                {icon}
            </div>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-arcdom-gold focus:border-arcdom-gold block w-full pl-10 p-3"
                placeholder={placeholder}
                required={required}
            />
        </div>
    );
};

const TextareaField = ({ icon, placeholder, name, value, onChange, required }) => {
    return (
        <div className="relative mb-6">
            <div className="absolute top-3 left-0 flex items-start pl-3 pointer-events-none text-gray-400">
                {icon}
            </div>
            <textarea
                name={name}
                value={value}
                onChange={onChange}
                rows="5"
                className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-arcdom-gold focus:border-arcdom-gold block w-full pl-10 p-3"
                placeholder={placeholder}
                required={required}
            ></textarea>
        </div>
    );
};

const Contact = () => {
    const formRef = useRef();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        interest: '',
        message: '',
    });

    const [formStatus, setFormStatus] = useState({
        submitted: false,
        success: false,
        message: '',
        isSubmitting: false,
    });

    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormStatus({ ...formStatus, isSubmitting: true, submitted: false });

        const templateParams = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            interest: formData.interest,
            message: formData.message,
        };

        emailjs.send(
            'service_d8r1iul',
            'template_bfkzy1l',
            templateParams,
            'Kpu2PwVj05mXaEH8u'
        )
            .then((result) => {
                setFormStatus({
                    submitted: true,
                    success: true,
                    message: 'Thank you for your message! We will contact you soon.',
                    isSubmitting: false,
                });
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    interest: '',
                    message: '',
                });
            }, (error) => {
                console.error('EmailJS FAILED...', error);
                setFormStatus({
                    submitted: true,
                    success: false,
                    message: 'There was an error sending your message. Please try again later.',
                    isSubmitting: false,
                });
            });
    };

    const interestOptions = [
        { value: '', label: 'Select your interest' },
        { value: 'Apartment Purchase', label: 'Apartment Purchase' },
        { value: 'House Purchase (Duplex)', label: 'House Purchase (Duplex)' },
        { value: 'Investment Opportunity', label: 'Investment Opportunity' },
        { value: 'Business Partnership', label: 'Business Partnership' },
        { value: 'General Information', label: 'General Information' },
        { value: 'Other Inquiry', label: 'Other Inquiry' },
    ];

    return (
        <section id="contact" className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Contact Us</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        Have questions or looking for more information about our projects?
                        Get in touch with our team, and we'll be happy to assist you.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6 }}
                        className="bg-gray-50 p-8 rounded-lg shadow-lg"
                    >
                        <h3 className="text-2xl font-semibold text-arcdom-navy mb-6">Send Us a Message</h3>

                        {formStatus.submitted && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`p-4 mb-6 rounded-lg text-sm ${
                                    formStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}
                            >
                                {formStatus.message}
                            </motion.div>
                        )}
                        <form onSubmit={handleSubmit} ref={formRef}>
                            <InputField
                                icon={<FiUser />}
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                value={formData.name}
                                onChange={handleChange}
                                required={true}
                            />
                            <InputField
                                icon={<FiMail />}
                                type="email"
                                name="email"
                                placeholder="Your Email"
                                value={formData.email}
                                onChange={handleChange}
                                required={true}
                            />
                            <InputField
                                icon={<FiPhone />}
                                type="tel"
                                name="phone"
                                placeholder="Your Phone Number (Optional)"
                                value={formData.phone}
                                onChange={handleChange}
                                required={false}
                            />
                            <div className="relative mb-6">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                                    <FiHome />
                                </div>
                                <select
                                    name="interest"
                                    value={formData.interest}
                                    onChange={handleChange}
                                    className="bg-white border border-gray-300 text-gray-900 text-base rounded-lg focus:ring-arcdom-gold focus:border-arcdom-gold block w-full pl-10 p-3"
                                    required
                                >
                                    {interestOptions.map((option) => (
                                        <option key={option.value} value={option.value} disabled={option.value === ''}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <TextareaField
                                icon={<FiMessageSquare />}
                                name="message"
                                placeholder="Your Message"
                                value={formData.message}
                                onChange={handleChange}
                                required={true}
                            />
                            <button
                                type="submit"
                                className="w-full bg-arcdom-gold text-arcdom-navy py-3 px-6 rounded-lg font-medium hover:bg-opacity-90 transition-all duration-300 disabled:opacity-50"
                                disabled={formStatus.isSubmitting}
                            >
                                {formStatus.isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay:0.1 }}
                        className="rounded-lg overflow-hidden shadow-lg h-full hidden lg:block"
                    >
                        <div className="relative h-full min-h-[500px]">
                            <img
                                src="/contact.jpg"
                                alt="Arcdom Office or Contact Scene"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </motion.div>
                </div>

                {/*<motion.div*/}
                {/*    initial={{ opacity: 0, y: 30 }}*/}
                {/*    whileInView={{ opacity: 1, y: 0 }}*/}
                {/*    viewport={{ once: true, amount: 0.2 }}*/}
                {/*    transition={{ duration: 0.6, delay: 0.3 }}*/}
                {/*    className="mt-20 p-8 bg-arcdom-navy rounded-lg text-white text-center"*/}
                {/*>*/}
                {/*    <h3 className="text-2xl font-semibold mb-4">Subscribe to Our Newsletter</h3>*/}
                {/*    <p className="mb-6 text-gray-300 max-w-2xl mx-auto">*/}
                {/*        Stay updated with our latest projects, offers, and real estate insights.*/}
                {/*    </p>*/}
                {/*    <form className="flex flex-col sm:flex-row max-w-lg mx-auto">*/}
                {/*        <input*/}
                {/*            type="email"*/}
                {/*            placeholder="Your Email Address"*/}
                {/*            aria-label="Email for newsletter"*/}
                {/*            className="flex-grow px-4 py-3 rounded-l-lg sm:rounded-r-none text-gray-900 focus:outline-none focus:ring-2 focus:ring-arcdom-gold mb-2 sm:mb-0"*/}
                {/*        />*/}
                {/*        <button type="submit" className="bg-arcdom-gold text-arcdom-navy px-6 py-3 rounded-r-lg sm:rounded-l-none font-medium hover:bg-opacity-90 transition-all duration-300">*/}
                {/*            Subscribe*/}
                {/*        </button>*/}
                {/*    </form>*/}
                {/*</motion.div>*/}
            </div>
        </section>
    );
};

export default Contact;
